import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { candidatePortalApi, CandidateAccount } from '../../api/candidatePortal';

interface CandidateAuthValue {
  account: CandidateAccount | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const CandidateAuthContext = createContext<CandidateAuthValue | null>(null);

/**
 * Genuinely separate from the internal AuthContext (auth/AuthContext.tsx)
 * - checks the candidate-portal session only (/api/public/auth/me),
 * never the internal Entra session, and never triggers any redirect
 * on its own since /auth/me is deliberately non-rejecting.
 */
export function CandidateAuthProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<CandidateAccount | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const result = await candidatePortalApi.me();
      setAccount(result.data);
    } catch {
      setAccount(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const logout = async () => {
    await candidatePortalApi.logout();
    setAccount(null);
  };

  return <CandidateAuthContext.Provider value={{ account, loading, refresh, logout }}>{children}</CandidateAuthContext.Provider>;
}

export function useCandidateAuth(): CandidateAuthValue {
  const ctx = useContext(CandidateAuthContext);
  if (!ctx) throw new Error('useCandidateAuth must be used within CandidateAuthProvider');
  return ctx;
}
